import React, { useState, useEffect, useCallback, useRef } from "react";
import steamService from "../../service/steam.service";
import backendApiService from "../../service/backend-api.service";
import SBPPaymentPage from "./SBPPaymentPage";
import PersonalDataConsent from "../../legal/PersonalDataConsent";
import OfferNotice from "../../legal/OfferNotice";

export const STEAM_RUB_MIN = 150;
export const STEAM_RUB_MAX = 25000;

/** Сумма пополнения под цену игры: целые рубли в пределах лимитов. */
export function topupRub(value) {
  return Math.min(STEAM_RUB_MAX, Math.max(STEAM_RUB_MIN, Math.ceil(value)));
}

/** initialRub / initialKzt — сумма под конкретную игру; тенге пересчитываются, когда придёт курс. */
const SteamTopUpWidget = ({ initialRub, initialKzt } = {}) => {
  const [steamAccount, setSteamAccount] = useState("");
  const [amountRub, setAmountRub] = useState(() => (initialRub > 0 ? topupRub(initialRub) : 500));
  const pendingKzt = useRef(initialKzt > 0 ? initialKzt : null);
  const [isLoading, setIsLoading] = useState(false);
  const [accountValidation, setAccountValidation] = useState(null);
  const [ratesLastUpdated, setRatesLastUpdated] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [pricedRub, setPricedRub] = useState(null);
  const [approxUsd, setApproxUsd] = useState(0);
  const [steamUsdCourse, setSteamUsdCourse] = useState(0);
  const [kztPerUsd, setKztPerUsd] = useState(0);
  const [usdInput, setUsdInput] = useState("");
  const [kztInput, setKztInput] = useState("");
  const [step, setStep] = useState(1);
  const [orderInfo, setOrderInfo] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [markupPercentage, setMarkupPercentage] = useState(10);
  const [isCryptoPayment, setIsCryptoPayment] = useState(false);
  const [consent, setConsent] = useState(false);
  const [cryptoAmount, setCryptoAmount] = useState(0);
  const [cryptoCommissionPercentage, setCryptoCommissionPercentage] = useState(4);
  const priceRequestId = useRef(0);
  const priceRequestController = useRef(null);
  const calculatePricesRef = useRef(null);

  const topupAmountValid = amountRub >= STEAM_RUB_MIN && amountRub <= STEAM_RUB_MAX;
  const priceReady = pricedRub === amountRub && payAmount > 0;

  const scrollToDescription = (e) => {
    e.preventDefault();
    const element = document.getElementById("steam-description");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const loadCryptoCommissionSettings = useCallback(async () => {
    try {
      const response = await backendApiService.getCryptoCommission();
      if (response.success) {
        setCryptoCommissionPercentage(response.commissionPercentage || 4);
      }
    } catch {
      /* default commission */
    }
  }, []);

  const clampRub = (value) => {
    const rounded = Math.round(value * 100) / 100;
    if (rounded > STEAM_RUB_MAX) return STEAM_RUB_MAX;
    if (rounded < 0) return 0;
    return rounded;
  };

  const syncEquivFromRub = (usd, kzt, course) => {
    setApproxUsd(usd);
    if (course > 0) setSteamUsdCourse(course);
    if (usd > 0 && kzt > 0) setKztPerUsd(kzt / usd);
    setUsdInput(usd > 0 ? usd.toFixed(2) : "");
    setKztInput(kzt > 0 ? kzt.toFixed(2) : "");
  };

  const calculatePrices = useCallback(async () => {
    priceRequestController.current?.abort();
    const requestId = ++priceRequestId.current;
    const controller = new AbortController();
    priceRequestController.current = controller;
    const isStale = () => requestId !== priceRequestId.current;
    setPricedRub(null);

    try {
      if (
        !amountRub ||
        typeof amountRub !== "number" ||
        isNaN(amountRub) ||
        !isFinite(amountRub)
      ) {
        setPayAmount(0);
        setApproxUsd(0);
        setUsdInput("");
        setKztInput("");
        setCryptoAmount(0);
        return;
      }

      if (amountRub < STEAM_RUB_MIN) {
        setPayAmount(0);
        setCryptoAmount(0);
        return;
      }

      const priceResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/currency/calculate-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amountRUB: amountRub, steamRegion: "RUB" }),
        signal: controller.signal,
      });
      const priceData = priceResponse.ok ? await priceResponse.json() : null;
      if (isStale()) return;

      if (priceData) {
        const usd = Number(priceData.approxUsd) || 0;
        const kzt = Number(priceData.approxKzt) || 0;
        const course = Number(priceData.steamUsdCourse) || 0;

        setPayAmount(priceData.rubPrice);
        setPricedRub(amountRub);
        syncEquivFromRub(usd, kzt, course);
        setRatesLastUpdated(new Date(priceData.timestamp || Date.now()));

        if (priceData.markup !== undefined) {
          setMarkupPercentage(priceData.markup);
        }

        if (usd > 0) {
          setCryptoAmount(Math.round(usd * (1 + cryptoCommissionPercentage / 100) * 100) / 100);
        } else {
          setCryptoAmount(0);
        }
      } else {
        setPayAmount(0);
        setApproxUsd(0);
        setUsdInput("");
        setKztInput("");
        setCryptoAmount(0);
      }
    } catch {
      if (isStale()) return;
      setPayAmount(0);
      setApproxUsd(0);
      setUsdInput("");
      setKztInput("");
      setCryptoAmount(0);
    }
  }, [amountRub, cryptoCommissionPercentage]);

  useEffect(() => {
    calculatePricesRef.current = calculatePrices;
  }, [calculatePrices]);

  useEffect(() => {
    loadCryptoCommissionSettings();

    const eventSource = new EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/currency/events`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "crypto_commission" && data.commissionPercentage !== undefined) {
          setCryptoCommissionPercentage(data.commissionPercentage);
        } else if (data.type === "markup_percentage" && data.markupPercentage !== undefined) {
          setMarkupPercentage(data.markupPercentage);
        } else if (data.type === "rates") {
          calculatePricesRef.current?.();
        }
      } catch {
        /* ignore */
      }
    };

    eventSource.onerror = () => {};

    return () => {
      eventSource.close();
    };
  }, [loadCryptoCommissionSettings]);

  useEffect(() => {
    calculatePrices();
  }, [calculatePrices]);

  useEffect(() => () => priceRequestController.current?.abort(), []);

  useEffect(() => {
    if (!pendingKzt.current || !kztPerUsd || !steamUsdCourse) return;
    const kzt = pendingKzt.current;
    pendingKzt.current = null;
    setAmountRub(topupRub((kzt / kztPerUsd) * steamUsdCourse));
  }, [kztPerUsd, steamUsdCourse]);

  useEffect(() => {
    if (isCryptoPayment) {
      loadCryptoCommissionSettings();
    }
  }, [isCryptoPayment, loadCryptoCommissionSettings]);

  const validateSteamAccount = async () => {
    if (!steamAccount.trim()) {
      setAccountValidation({
        isValid: false,
        message: "Введите логин Steam аккаунта",
      });
      return;
    }

    if (amountRub < STEAM_RUB_MIN) {
      setAccountValidation({
        isValid: false,
        message: `Минимальная сумма для пополнения: ${STEAM_RUB_MIN} ₽`,
      });
      return;
    }

    if (amountRub > STEAM_RUB_MAX) {
      setAccountValidation({
        isValid: false,
        message: `Максимальная сумма для пополнения: ${STEAM_RUB_MAX} ₽`,
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await steamService.verifySteamAccount(steamAccount);
      setAccountValidation(result);
    } catch {
      setAccountValidation({
        isValid: false,
        message: "Ошибка проверки аккаунта",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePayment = async () => {
    if (!priceReady || !consent) return;
    setIsLoading(true);
    try {
      setCheckout({
        amountRub: pricedRub,
        payAmount,
        amountUsd: approxUsd,
        cryptoAmount,
        isCrypto: isCryptoPayment,
        consent,
      });
      setStep(3);
    } catch {
      alert("Ошибка создания платежа");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = (result) => {
    setOrderInfo({
      orderId: result.orderId || result.order_id,
      steamAccount: result.steamAccount || steamAccount,
      amountRub: checkout?.amountRub ?? amountRub,
      message: result.message || "Steam аккаунт будет пополнен в ближайшее время",
    });
    setStep(2);
  };

  const handlePaymentFail = (error) => {
    alert(error);
    setStep(1);
  };

  const handleBackToMain = () => {
    setStep(1);
  };

  const handleRubAmountChange = (e) => {
    let value = e.target.value;

    if (value === "") {
      setAmountRub(0);
      setPayAmount(0);
      setApproxUsd(0);
      setUsdInput("");
      setKztInput("");
      setCryptoAmount(0);
      return;
    }

    value = value.replace(",", ".");

    const decimalRegex = /^\d+(\.\d{0,2})?$/;
    if (!decimalRegex.test(value)) {
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setAmountRub(clampRub(numValue));
    }
  };

  const handleUsdInputChange = (e) => {
    const raw = e.target.value.replace(",", ".");
    if (raw !== "" && !/^\d*(\.\d{0,2})?$/.test(raw)) return;

    setUsdInput(raw);
    if (raw === "" || !steamUsdCourse) return;

    const usd = parseFloat(raw);
    if (!Number.isFinite(usd) || usd <= 0) return;

    if (kztPerUsd > 0) {
      setKztInput((usd * kztPerUsd).toFixed(2));
    }
    setAmountRub(clampRub(usd * steamUsdCourse));
  };

  const handleKztInputChange = (e) => {
    const raw = e.target.value.replace(",", ".");
    if (raw !== "" && !/^\d*(\.\d{0,2})?$/.test(raw)) return;

    setKztInput(raw);
    if (raw === "" || !kztPerUsd || !steamUsdCourse) return;

    const kzt = parseFloat(raw);
    if (!Number.isFinite(kzt) || kzt <= 0) return;

    const usd = kzt / kztPerUsd;
    setUsdInput(usd.toFixed(2));
    setAmountRub(clampRub(usd * steamUsdCourse));
  };

  const equivInputStyle = (value, minCh = 4) => ({
    width: `${Math.max(String(value || "").length, minCh) + 0.5}ch`,
  });

  const equivInputClass =
    "inline-block min-w-0 max-w-[8rem] px-0.5 py-0 text-xs leading-tight text-center tabular-nums text-gray-600 dark:text-gray-300 bg-transparent border-0 border-b border-dashed border-gray-400/70 dark:border-gray-500 hover:border-blue-400 focus:border-blue-500 focus:outline-none transition-colors cursor-text";

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Steam для пополнения{" "}
              <button
                type="button"
                onClick={scrollToDescription}
                className="text-blue-600 dark:text-blue-400 hover:underline font-normal"
              >
                (Россия+СНГ)
              </button>
            </label>
            <div className="relative">
              <input
                type="text"
                value={steamAccount}
                onChange={(e) => setSteamAccount(e.target.value)}
                placeholder="Введите логин Steam"
                disabled={accountValidation?.isValid}
                className={`ym-disable-keys w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  accountValidation?.isValid
                    ? "border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 cursor-not-allowed"
                    : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                }`}
              />
              {accountValidation?.isValid && (
                <button
                  onClick={() => {
                    setAccountValidation(null);
                    setSteamAccount("");
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 transition-colors"
                  title="Изменить логин"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              )}
            </div>
            <div className="mt-1">
              <span
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById("how-to-find-login");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer inline-block"
              >
                что такое логин?***
              </span>
            </div>
          </div>

          {accountValidation && (
            <div
              className={`p-3 rounded-md ${
                accountValidation.isValid
                  ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                  : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
              }`}
            >
              <div className="flex items-center">
                {accountValidation.isValid ? (
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span>{accountValidation.message}</span>
              </div>
              {accountValidation.steamId && (
                <div className="text-xs mt-1 opacity-75">Steam ID: {accountValidation.steamId}</div>
              )}
            </div>
          )}

          <button
            onClick={validateSteamAccount}
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition duration-200"
          >
            {isLoading ? "Проверяем..." : "Подтвердить"}
          </button>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Сумма пополнения (В рублях)
            </label>
            <input
              type="number"
              value={amountRub || ""}
              onChange={handleRubAmountChange}
              max={STEAM_RUB_MAX}
              step="1"
              placeholder={`Введите сумму (${STEAM_RUB_MIN}-${STEAM_RUB_MAX} ₽)`}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Минимальная сумма: {STEAM_RUB_MIN} ₽, Максимальная: {STEAM_RUB_MAX} ₽
            </div>
            {amountRub > 0 && amountRub < STEAM_RUB_MIN && (
              <div className="mt-2 p-2 bg-yellow-100 dark:bg-yellow-900 border border-yellow-300 dark:border-yellow-700 rounded-md">
                <span className="text-sm text-yellow-800 dark:text-yellow-200">
                  Минимальная сумма для пополнения: {STEAM_RUB_MIN} ₽
                </span>
              </div>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/80 px-3 py-2.5 rounded-md">
            <div className="text-center space-y-1.5">
              <div>
                <div className="text-base font-semibold text-gray-700 dark:text-gray-200">
                  В Steam вы получите:
                </div>
                <div className="text-[1.375rem] font-semibold text-blue-600 dark:text-blue-400 leading-tight">
                  {amountRub > 0 ? `${amountRub.toFixed(2)} ₽` : "—"}
                </div>
                {steamUsdCourse > 0 && amountRub > 0 && (
                  <div
                    className="mt-1 flex flex-wrap items-baseline justify-center gap-x-1.5 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400"
                    title="Можно изменить $ или ₸"
                  >
                    <span className="text-gray-400 dark:text-gray-500">или</span>
                    <span className="inline-flex items-baseline gap-px">
                      <span>≈&nbsp;$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={usdInput}
                        onChange={handleUsdInputChange}
                        style={equivInputStyle(usdInput, 4)}
                        className={equivInputClass}
                        aria-label="Сумма в долларах"
                      />
                    </span>
                    {kztPerUsd > 0 && (
                      <span className="inline-flex items-baseline gap-px">
                        <span>≈&nbsp;</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={kztInput}
                          onChange={handleKztInputChange}
                          style={equivInputStyle(kztInput, 5)}
                          className={equivInputClass}
                          aria-label="Сумма в тенге"
                        />
                        <span>&nbsp;₸*</span>
                      </span>
                    )}
                  </div>
                )}
                {amountRub >= STEAM_RUB_MIN && steamUsdCourse <= 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Загрузка курса...</div>
                )}
                {steamUsdCourse > 0 && (
                  <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    * примерный курс · можно править
                  </div>
                )}
              </div>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-1.5">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Стоимость услуги:</div>
                <div
                  className={`text-[1.25rem] font-semibold leading-tight ${
                    isCryptoPayment
                      ? "text-purple-600 dark:text-purple-400"
                      : "text-green-600 dark:text-green-400"
                  }`}
                >
                  {topupAmountValid && !priceReady
                    ? "…"
                    : isCryptoPayment
                      ? `${cryptoAmount} USDT`
                      : `${payAmount} ₽`}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {isCryptoPayment ? (
                    <>
                      (${approxUsd.toFixed(2)} +{" "}
                      <span className="text-purple-600 dark:text-purple-400 font-medium">
                        {cryptoCommissionPercentage}% комиссия
                      </span>
                      )
                    </>
                  ) : (
                    `(${amountRub} ₽ + ${markupPercentage}% комиссия)`
                  )}
                </div>
              </div>
              {ratesLastUpdated && (
                <div className="border-t border-gray-200 dark:border-gray-600 pt-1">
                  <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                    <span>Курсы: {ratesLastUpdated.toLocaleTimeString("ru-RU")}</span>
                    <button
                      type="button"
                      onClick={() => calculatePrices()}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                    >
                      обновить
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <PersonalDataConsent
            checked={consent}
            onChange={setConsent}
            className="flex cursor-pointer items-start gap-2 text-sm"
            inputClassName="mt-0.5 shrink-0 accent-blue-600"
            textClassName="text-gray-700 dark:text-gray-300"
            linkClassName="text-blue-600 underline underline-offset-2 dark:text-blue-400"
          />

          <button
            onClick={handleCreatePayment}
            disabled={
              isLoading ||
              !topupAmountValid ||
              !accountValidation?.isValid ||
              !consent ||
              !priceReady ||
              (isCryptoPayment && cryptoAmount <= 0)
            }
            className={`w-full ${
              isCryptoPayment ? "bg-purple-600 hover:bg-purple-700" : "bg-green-600 hover:bg-green-700"
            } disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition duration-200`}
          >
            {isLoading
              ? "Создаем платеж..."
              : !topupAmountValid
                ? `Минимальная сумма ${STEAM_RUB_MIN} ₽`
                : !accountValidation?.isValid
                  ? "Подтвердите Steam аккаунт"
                  : !consent
                    ? "Дайте согласие на обработку данных"
                    : !priceReady
                      ? "Считаем стоимость..."
                      : isCryptoPayment
                        ? `Оплатить ${cryptoAmount} USDT`
                        : `Оплатить ${payAmount} ₽`}
          </button>

          <OfferNotice className="legal-notice" linkClassName="legal-notice-link" />

          <div className="mt-2 text-center">
            <label className="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={isCryptoPayment}
                onChange={(e) => setIsCryptoPayment(e.target.checked)}
                className="w-3 h-3 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-1 dark:bg-gray-700 dark:border-gray-600 mr-1"
              />
              <span>
                Оплата криптой{" "}
                <span className="text-purple-600 dark:text-purple-400 font-medium">
                  комиссия {cryptoCommissionPercentage}%
                </span>
              </span>
            </label>
          </div>
        </div>
      )}

      {step === 3 && checkout && (
        <SBPPaymentPage
          amount={checkout.isCrypto ? checkout.cryptoAmount : checkout.payAmount}
          amountUSD={checkout.amountUsd}
          amountRUB={checkout.amountRub}
          steamRegion="RUB"
          steamAccount={steamAccount}
          isCryptoPayment={checkout.isCrypto}
          cryptoAmount={checkout.cryptoAmount}
          consent={checkout.consent}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentFail={handlePaymentFail}
          onBack={handleBackToMain}
        />
      )}

      {step === 2 && (
        <div className="space-y-4 text-center">
          <div className="text-green-600 dark:text-green-400">
            <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h3 className="text-xl font-semibold mb-2">Платеж успешен!</h3>

            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-4">
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <span className="font-medium">Steam аккаунт:</span> {orderInfo?.steamAccount || steamAccount}
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <span className="font-medium">Будет пополнен на:</span>{" "}
                <span className="font-bold text-blue-600">{orderInfo?.amountRub || amountRub} ₽</span>
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <span className="font-medium">Сумма к оплате:</span>
                <span
                  className={
                    checkout?.isCrypto
                      ? "text-purple-600 dark:text-purple-400 font-medium"
                      : "text-gray-600 dark:text-gray-400"
                  }
                >
                  {checkout?.isCrypto ? ` ${checkout.cryptoAmount} USDT` : ` ${checkout?.payAmount ?? payAmount} ₽`}
                </span>
              </p>
              {orderInfo?.orderId && (
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  <span className="font-medium">Номер заказа:</span> {orderInfo.orderId}
                </p>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-left">
                  <p className="text-blue-800 dark:text-blue-200 text-sm font-medium mb-1">
                    {orderInfo?.message || "Steam аккаунт будет пополнен в ближайшее время"}
                  </p>
                  <p className="text-blue-700 dark:text-blue-300 text-xs">
                    Если в течение часа пополнения не будет, сообщите номер заказа{" "}
                    <span className="font-mono font-bold">{orderInfo?.orderId || "XXXXX"}</span> в техподдержку:{" "}
                    <a href={process.env.REACT_APP_SUPPORT_URL} className="underline hover:no-underline">
                      @tp_trendy_eng_shop
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleBackToMain}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200"
          >
            Создать новый платеж
          </button>
        </div>
      )}
    </div>
  );
};

export default SteamTopUpWidget;
